import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { agrupar, descreverRegra, notacao } from '@/lib/notacaoDados'
import { DadoCubo, COR_DO_DADO } from './DadoCubo'
import { ResultadoDaJogada, RITMO } from './ResultadoDaJogada'
import type { JogadaExibida } from './ResultadoDaJogada'
import { DICE_TYPES, MAX_DADOS_POR_JOGADA } from '@/types/dice'
import type {
  AtributoTestavel, DiceGroup, DiceRule, DiceRollResult, DiceType, SavedRoll,
} from '@/types/dice'

/**
 * Bandeja de dados (fase 3 de docs/PLANO_MODULO_DADOS.md; fase A do redesenho
 * na §10 do mesmo plano).
 *
 * O jogador MONTA a jogada tocando nos dados e so entao rola. Duas coisas
 * deliberadas aqui:
 *
 * 1. **A notacao e gerada do gesto, nunca digitada.** Ela aparece para ser
 *    lida ("2d6 + 1d20"), e o jogador nao precisa aprender sintaxe nenhuma.
 * 2. **A regra e da mesa, nao da jogada.** Quem define se a mesa soma tudo,
 *    mantem os maiores ou conta sucessos e o mestre, na configuracao. Aqui ela
 *    so aparece escrita, para o jogador saber como sera resolvida.
 *
 * **A MESMA tela para a mesa inteira (fase A, decisao do Paulo, 2026-07-26).**
 * A bandeja tem dois modos, e e UM componente de proposito — este modulo ja
 * foi mordido tres vezes por telas irmas que envelheceram separadas:
 *
 * - sem `espelho`: a bandeja de quem monta — interativa, rola e envia;
 * - com `espelho`: a bandeja da MESA — a mesma anatomia, somente leitura,
 *   recebendo por props o que quem monta escolheu, o giro e o resultado.
 *
 * Bandeja de UM dado envia pelo caminho de sempre (`dice_type`), e nao como
 * bandeja: rolar um d20 continua sendo exatamente o que sempre foi, inclusive
 * no registro e no chat.
 *
 * **A VALIDACAO DO MESTRE (fase B, 2026-07-26).** A jogada rolada nao vira
 * registro sozinha: quem fecha o momento e o mestre da mesa. Consequencias
 * nesta tela — todas decisao do Paulo, nenhuma cosmetica:
 *
 * - o jogador comum nao tem mais botao de enviar: ele ve "Aguardando o mestre
 *   validar..." (o mestre, na PROPRIA bandeja, mantem o dele — auto-validada);
 * - no espelho, o mestre tem DOIS caminhos: "Validar e enviar" e "Pedir para
 *   repetir" — e a validacao e sobre a MONTAGEM, nunca sobre o numero;
 * - o envio automatico de 12s morreu: o prazo agora e do SERVIDOR;
 * - repetir devolve esta bandeja a montagem COM os dados que estavam nela, sem
 *   fechar tela nenhuma.
 *
 * Desenhada mobile-first: o alvo de toque tem 44px, a paleta quebra em linhas
 * e nada depende de hover.
 */

/** A bandeja vista por quem NAO esta montando: tudo chega por props. */
export interface EspelhoDaBandeja {
  /** Nome de exibicao de quem monta. */
  nome: string
  dice: DiceType[]
  modifiers: { id: string; name: string; value: number }[]
  /** Os dados estao no ar (o inicio chegou, o resultado ainda nao). */
  rolando: boolean
  /** A jogada resolvida, quando o fim chegou. */
  jogada: JogadaExibida | null
  rollId?: string
  /** Quem ve e o mestre: valida a jogada ou pede para repetir (fase B). */
  possoEnviar?: boolean
  rotuloDeEnvio?: string
}

interface DiceTrayProps {
  regra?: DiceRule | null
  disabled?: boolean
  /** Atributos da ficha de quem esta rolando (fase 4). Vazio = sem chips. */
  atributos?: AtributoTestavel[]
  /** Rolagens salvas da campanha (catalogo do game_config). */
  salvas?: SavedRoll[]
  /** Envia a jogada. Recebe os grupos e os atributos escolhidos. */
  onRolar: (grupos: DiceGroup[], atributosEscolhidos: string[]) => Promise<DiceRollResult | null>
  /** Publica no chat a jogada que já rolou. Devolve se conseguiu. */
  onEnviar?: (rollId: string) => Promise<boolean>
  /** O mestre pede para repetir a jogada (fase B). Devolve se conseguiu. */
  onRepetir?: (rollId: string) => Promise<boolean>
  /** Jogada que rolou e nao foi enviada (o jogador fechou a tela no meio). */
  pendente?: DiceRollResult | null
  /** Cada mudanca na montagem, para espelhar na mesa (fase A). */
  onMudanca?: (dados: DiceType[], atributosEscolhidos: string[]) => void
  /** Presente = esta e a bandeja da MESA, nao a de quem monta. */
  espelho?: EspelhoDaBandeja | null
  /** Quem esta com esta bandeja e o mestre da mesa: a jogada dele e
   *  auto-validada, e ele mantem o proprio botao de enviar (fase B). */
  souMestre?: boolean
  /** Ha mestre na mesa? Sem ele o servidor recusa a rolagem; a UI explica
   *  antes de o jogador tentar (decisao 1 do Paulo, 2026-07-26). */
  mestreOnline?: boolean
  /** Id da jogada que o mestre mandou repetir: a bandeja volta a montagem
   *  mantendo os dados e chips que estavam montados. */
  repetida?: string | null
  /**
   * Teste PEDIDO pelo mestre: a bandeja nasce montada com o que ele definiu.
   *
   * O bloqueio vem ANTES (decisao do Paulo, 2026-07-31): o desafio e do mestre,
   * e por isso os dados nao sao editaveis aqui. Mas quem rola continua pondo a
   * mao neles — ve os cubos na bandeja, toca em Rolar e assiste ao giro no
   * mesmo lugar. O que morreu foi o botao "Rolar D20" pelado, que dizia ao
   * jogador que ele era um gatilho.
   */
  pedido?: { dice: DiceType[]; atributoId?: string } | null
}

export function DiceTray({
  regra,
  disabled = false,
  atributos = [],
  salvas = [],
  onRolar,
  onEnviar,
  onRepetir,
  pendente = null,
  onMudanca,
  espelho = null,
  souMestre = false,
  mestreOnline = true,
  repetida = null,
  pedido = null,
}: DiceTrayProps) {
  const [dadosLocais, setDadosLocais] = useState<DiceType[]>(pedido?.dice ?? [])
  // Chips de atributo: quais atributos da ficha entram nesta jogada.
  const [refs, setRefs] = useState<string[]>(
    pedido?.atributoId ? [pedido.atributoId] : []
  )
  const [rolando, setRolando] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)
  // A jogada acontece AQUI: os mesmos cubos que voce montou chacoalham e
  // assentam nesta tela, sem nenhuma outra pulando por cima. A tela que
  // seleciona e a que rola e a que mostra (decisao do Paulo, 2026-07-25).
  const [jogada, setJogada] = useState<DiceRollResult | null>(pendente)
  const [resolvido, setResolvido] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [repetindo, setRepetindo] = useState(false)

  // Espelho: o resultado chega ~junto com o inicio (o servidor responde em
  // ~100ms). Segurar aqui e o que da tempo de VER os dados girarem — a mesma
  // espera de quem rolou. Quem abriu DEPOIS de a jogada resolver nao espera.
  const [espelhoPodeMostrar, setEspelhoPodeMostrar] = useState(false)
  const desdeQuandoGira = useRef<number | null>(null)

  useEffect(() => {
    if (!espelho) return
    if (espelho.rolando) {
      if (desdeQuandoGira.current === null) desdeQuandoGira.current = Date.now()
      setEspelhoPodeMostrar(false)
      return
    }
    if (!espelho.jogada) {
      desdeQuandoGira.current = null
      setEspelhoPodeMostrar(false)
      return
    }
    if (espelhoPodeMostrar) return
    if (desdeQuandoGira.current === null) {
      // nunca viu o giro (entrou depois): mostra direto
      setEspelhoPodeMostrar(true)
      return
    }
    const jaGirou = Date.now() - desdeQuandoGira.current
    const falta = Math.max(0, RITMO.chacoalho - jaGirou)
    const t = setTimeout(() => {
      desdeQuandoGira.current = null
      setEspelhoPodeMostrar(true)
    }, falta)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [espelho?.rolando, espelho?.jogada, espelhoPodeMostrar])

  // Jogada que ficou pelo caminho (o jogador fechou a bandeja sem enviar):
  // volta para a tela ao reabrir, com o botao de enviar esperando.
  useEffect(() => {
    if (pendente) setJogada(pendente)
  }, [pendente])

  // O que esta na tela — a MESMA conta nos dois modos.
  const jogadaExibida: JogadaExibida | null = espelho
    ? (espelhoPodeMostrar ? espelho.jogada : null)
    : (jogada && {
        spec: jogada.dice_spec
          ?? [{ type: jogada.dice_type, result: jogada.result }],
        kept: jogada.kept,
        rule: jogada.rule,
        expression: jogada.expression,
        modifiers: jogada.modifiers,
        attributeName: jogada.attribute_name,
        attributeValue: jogada.attribute_value,
        bonus: jogada.bonus,
        result: jogada.result,
        total: jogada.total,
        difficulty: jogada.difficulty,
        outcome: jogada.outcome,
        actionName: jogada.action_name,
      })
  const girando = espelho
    ? (espelho.rolando || (!!espelho.jogada && !espelhoPodeMostrar))
    : rolando

  // A regra so e aplicada a vista DEPOIS de os dados assentarem: primeiro
  // todos os numeros aparecem, depois os descartados apagam.
  useEffect(() => {
    if (!jogadaExibida) { setResolvido(false); return }
    const t = setTimeout(() => setResolvido(true), RITMO.assentar + RITMO.resolver)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!jogadaExibida])

  // O envio automatico de 12s MORREU na fase B: quem fecha o momento e o
  // mestre, e um cronometro de navegador validando por ele seria mentira. O
  // prazo passou a ser do SERVIDOR (120s, publica sem validacao) — e por isso
  // nao ha mais nada a agendar nesta tela.

  // O mestre mandou repetir: a jogada sai de cena e a bandeja volta a
  // MONTAGEM com os dados e chips que estavam montados, para o jogador
  // corrigir o que foi apontado e rolar de novo. Nada de tela fechando.
  useEffect(() => {
    if (!repetida || !jogada || jogada.id !== repetida) return
    setJogada(null)
    setResolvido(false)
    setAviso('O mestre pediu para repetir a jogada. Ajuste e role de novo.')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repetida, jogada?.id])

  // Cada toque na montagem sai para a mesa ver (fase A). So quem monta emite,
  // e so enquanto ainda e montagem — jogada rolada nao e mais montagem.
  useEffect(() => {
    if (espelho || jogada || rolando) return
    onMudanca?.(dadosLocais, refs)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dadosLocais, refs])

  const dados = espelho ? espelho.dice : dadosLocais

  const parcelas = useMemo(
    () => espelho
      ? espelho.modifiers.map(m => ({ valor: m.value, origem: m.name }))
      : refs
          .map(id => atributos.find(a => a.id === id))
          .filter((a): a is AtributoTestavel => !!a)
          .map(a => ({ valor: a.value, origem: a.name })),
    [espelho, refs, atributos]
  )
  const expressao = useMemo(
    () => notacao(dados, regra, parcelas),
    [dados, regra, parcelas]
  )
  // Com a jogada na tela a bandeja para de aceitar mudanca: o que esta ali ja
  // aconteceu e a mesa ja viu. A saida e enviar. No espelho, tudo e leitura.
  const bloqueado = !!espelho || disabled || rolando || !!jogada || enviando
  // Num teste pedido, o desafio e do mestre: a montagem nao se edita. Isto NAO
  // e o mesmo que `bloqueado` — aqui o botao de rolar continua vivo, e e o
  // jogador quem o toca.
  const travada = !!pedido
  // O servidor e a autoridade sobre a presenca do mestre (403 no /roll); esta
  // tela so evita o caminho e EXPLICA. O mestre passa: ele e quem valida.
  const podeRolarSemMestre = mestreOnline || souMestre

  const adicionar = (tipo: DiceType) => {
    if (bloqueado || travada) return
    if (dadosLocais.length >= MAX_DADOS_POR_JOGADA) {
      setAviso(`Limite de ${MAX_DADOS_POR_JOGADA} dados por jogada.`)
      return
    }
    // Feedback tatil no proprio toque (Android vibra; iOS ignora em silencio)
    if ('vibrate' in navigator) navigator.vibrate(20)
    setAviso(null)
    setDadosLocais(atual => [...atual, tipo])
  }

  const remover = (indice: number) => {
    if (bloqueado || travada) return
    setAviso(null)
    setDadosLocais(atual => atual.filter((_, i) => i !== indice))
  }

  const limpar = () => {
    if (bloqueado || travada) return
    setDadosLocais([])
    setRefs([])
    setAviso(null)
  }

  const alternarAtributo = (id: string) => {
    if (bloqueado || travada) return
    setRefs(atual => atual.includes(id) ? atual.filter(r => r !== id) : [...atual, id])
  }

  /** Aplica uma rolagem salva: substitui a bandeja inteira, num toque so. */
  const aplicarSalva = (salva: SavedRoll) => {
    if (bloqueado || travada) return
    const expandidos: DiceType[] = []
    salva.dice.forEach(g => {
      for (let i = 0; i < g.qty; i++) expandidos.push(g.type)
    })
    setDadosLocais(expandidos.slice(0, MAX_DADOS_POR_JOGADA))
    setRefs(salva.attribute_ids.filter(id => atributos.some(a => a.id === id)))
    setAviso(null)
  }

  const rolar = async () => {
    if (bloqueado || !dadosLocais.length) return
    setRolando(true)
    setAviso(null)
    try {
      // O tempo do chacoalho e de LEITURA, nao de rede: o servidor responde em
      // ~100ms e sem esta espera o dado ja estaria parado antes de o jogador
      // perceber que tocou. Segura o resultado ate o minimo passar.
      const [resposta] = await Promise.all([
        onRolar(agrupar(dadosLocais), refs),
        new Promise(resolve => setTimeout(resolve, RITMO.chacoalho)),
      ])
      if (resposta) setJogada(resposta)
      else setAviso('Nao consegui rolar. Tente de novo.')
    } finally {
      setRolando(false)
    }
  }

  const enviarJogada = async (rollId: string) => {
    if (!onEnviar || enviando) return
    setEnviando(true)
    try {
      const foi = await onEnviar(rollId)
      if (foi) {
        setJogada(null)
        setDadosLocais([])
        setRefs([])
      } else {
        setAviso('Nao consegui enviar. Tente de novo.')
      }
    } finally {
      setEnviando(false)
    }
  }

  const enviar = () => { if (jogada) void enviarJogada(jogada.id) }

  const enviarDoEspelho = () => {
    if (espelho?.rollId) void enviarJogada(espelho.rollId)
  }

  /**
   * O mestre pede para repetir. A tela NAO fecha e a jogada nao e apagada: o
   * servidor a marca no historico e devolve a bandeja de quem rolou a
   * montagem (via broadcast). Aqui so avisamos se o pedido nao passou.
   */
  const pedirParaRepetir = async () => {
    const rollId = espelho?.rollId ?? jogada?.id
    if (!onRepetir || !rollId || repetindo) return
    setRepetindo(true)
    try {
      const foi = await onRepetir(rollId)
      if (!foi) setAviso('Nao consegui pedir a repeticao. Tente de novo.')
    } finally {
      setRepetindo(false)
    }
  }

  const contarPorTipo = (tipo: DiceType) => dados.filter(d => d === tipo).length

  // Chips: quem monta ve a propria ficha; a mesa ve o que foi marcado.
  const chips = espelho
    ? espelho.modifiers.map(m => ({ id: m.id, name: m.name, value: m.value, dentro: true }))
    : travada
      ? atributos.filter(a => refs.includes(a.id)).map(a => ({ ...a, dentro: true }))
      : atributos.map(a => ({ ...a, dentro: refs.includes(a.id) }))

  return (
    <div className="flex flex-col gap-4">
      {/* Regra da mesa: todos precisam saber como a jogada sera resolvida */}
      <p className="text-xs text-game-muted" data-testid="bandeja-regra">
        Regra da mesa: <span className="font-medium text-game-primary dark:text-zinc-100">
          {descreverRegra(regra)}
        </span>
      </p>

      {/* Paleta — a mesma para todos; no espelho ela so mostra as contagens.
          Num pedido do mestre ela SOME em vez de aparecer desabilitada: chip
          apagado a 50% se le como "nao selecionado", nao como "nao editavel"
          (licao do espelho, 2026-07-31). */}
      {!travada && (
      <div className="flex flex-wrap gap-2" data-testid="bandeja-paleta">
        {DICE_TYPES.map(tipo => {
          const quantos = contarPorTipo(tipo)
          return (
            <button
              key={tipo}
              type="button"
              disabled={bloqueado}
              onClick={() => adicionar(tipo)}
              aria-label={`adicionar ${tipo}`}
              className={cn(
                'relative min-h-11 min-w-14 px-3 rounded-xl border-2 font-mono text-sm font-medium',
                'bg-game-secondary transition-transform active:translate-y-px',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                COR_DO_DADO[tipo]
              )}
            >
              {tipo.toLowerCase()}
              {quantos > 0 && (
                <span
                  className="absolute -top-2 -right-2 min-w-5 h-5 px-1 rounded-full bg-game-primary text-game-secondary dark:bg-zinc-100 dark:text-zinc-900 text-[11px] font-bold flex items-center justify-center"
                  data-testid={`selo-${tipo}`}
                >
                  {quantos}
                </span>
              )}
            </button>
          )
        })}
      </div>
      )}

      {/* Chips de atributo (fase 4): a ficha entra na jogada */}
      {chips.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <p className="text-[11px] uppercase tracking-wider text-game-muted">
            somar da ficha
          </p>
          <div className="flex flex-wrap gap-2" data-testid="bandeja-atributos">
            {chips.map(chip => (
              <button
                key={chip.id}
                type="button"
                disabled={bloqueado || travada}
                aria-pressed={chip.dentro}
                onClick={() => alternarAtributo(chip.id)}
                className={cn(
                  'min-h-11 px-3 rounded-xl border text-sm transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  chip.dentro
                    ? 'border-violet-400 bg-violet-500/20 text-violet-200'
                    : 'border-game-border-primary bg-game-secondary text-game-secondary'
                )}
              >
                {chip.name}
                <span className="ml-1.5 font-mono opacity-80">
                  {chip.value >= 0 ? `+${chip.value}` : chip.value}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Rolagens salvas da campanha: so para quem monta */}
      {!espelho && !travada && salvas.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <p className="text-[11px] uppercase tracking-wider text-game-muted">
            rolagens da campanha
          </p>
          <div className="flex flex-wrap gap-2" data-testid="bandeja-salvas">
            {salvas.map(salva => (
              <button
                key={salva.id}
                type="button"
                disabled={bloqueado}
                onClick={() => aplicarSalva(salva)}
                className={cn(
                  'min-h-11 px-3 rounded-xl border border-dashed text-sm',
                  'border-game-border-primary text-game-secondary bg-game-secondary/50',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {salva.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bandeja */}
      <div className="rounded-2xl bg-game-tertiary/60 border border-game-border-primary p-3">
        <p
          className="text-center font-mono text-xs text-game-muted pb-2 mb-2 border-b border-game-border-primary break-words"
          data-testid="bandeja-notacao"
        >
          {expressao || 'bandeja vazia'}
        </p>

        {jogadaExibida ? (
          // A jogada aconteceu AQUI: os cubos que estavam montados chacoalham
          // e assentam no mesmo lugar. Nada de tela nova — em NENHUMA tela.
          <ResultadoDaJogada
            jogada={jogadaExibida}
            resolvido={resolvido}
            className="py-2"
          />
        ) : girando ? (
          // Os dados no ar. E o MESMO componente da linha de cima, de
          // proposito: assim os cubos que chacoalham sao os mesmos que vao
          // assentar, e o navegador consegue animar a passagem.
          <ResultadoDaJogada
            jogada={{ spec: dados.map(tipo => ({ type: tipo, result: 0 })) }}
            rolando
            className="py-2"
          />
        ) : dados.length === 0 ? (
          <p className="text-center text-sm text-game-muted py-4">
            {espelho
              ? `${espelho.nome} está escolhendo os dados...`
              : 'Toque nos dados acima para montar a jogada.'}
          </p>
        ) : (
          <div className="flex flex-wrap justify-center gap-2 py-1" data-testid="bandeja-dados">
            {dados.map((tipo, i) => (
              <button
                key={`${tipo}-${i}`}
                type="button"
                disabled={bloqueado || travada}
                onClick={() => remover(i)}
                aria-label={espelho || travada ? tipo.toLowerCase() : `remover ${tipo}`}
                title={espelho || travada ? undefined : 'remover este dado'}
                className={cn(
                  'flex flex-col items-center gap-1 transition-opacity',
                  'active:translate-y-px disabled:cursor-default',
                  // Dado de um pedido nao se apaga a 50%: ele E a jogada, e
                  // precisa ser lido como cubo cheio, nao como algo desligado.
                  travada && 'disabled:opacity-100',
                  !bloqueado && !travada && 'hover:opacity-70'
                )}
              >
                <DadoCubo tipo={tipo} />
                <span className="font-mono text-[10px] text-game-muted">
                  {tipo.toLowerCase()}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {aviso && (
        <p className="text-xs text-amber-400" role="status">{aviso}</p>
      )}

      {/* Acoes — quem monta age; o MESTRE fecha o momento (fase B) */}
      {espelho ? (
        <div className="flex items-center justify-center min-h-11">
          {jogadaExibida ? (
            espelho.possoEnviar && espelho.rollId ? (
              // O mestre valida a MONTAGEM (dado certo, atributo certo), nunca
              // o numero — ele nasce no servidor e ninguem o toca. Por isso os
              // dois caminhos ficam lado a lado, com o mesmo peso visual.
              <div className="flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={enviarDoEspelho}
                  disabled={enviando || repetindo}
                  data-testid="bandeja-validar"
                  className={cn(
                    'min-h-11 px-5 rounded-xl font-semibold',
                    'bg-emerald-600 text-white active:translate-y-px transition-transform',
                    'disabled:opacity-40 disabled:cursor-not-allowed'
                  )}
                >
                  {enviando ? 'Enviando...' : (espelho.rotuloDeEnvio ?? 'Validar e enviar')}
                </button>
                <button
                  type="button"
                  onClick={pedirParaRepetir}
                  disabled={enviando || repetindo}
                  data-testid="bandeja-repetir"
                  className={cn(
                    'min-h-11 px-5 rounded-xl font-medium',
                    'border border-amber-500/60 text-amber-300 bg-amber-500/10',
                    'active:translate-y-px transition-transform',
                    'disabled:opacity-40 disabled:cursor-not-allowed'
                  )}
                >
                  {repetindo ? 'Pedindo...' : 'Pedir para repetir'}
                </button>
              </div>
            ) : (
              <p className="text-xs text-game-muted" data-testid="bandeja-aguardando">
                Aguardando o mestre validar...
              </p>
            )
          ) : (
            <p className="text-xs text-game-muted" data-testid="bandeja-quem-monta">
              {girando
                ? `${espelho.nome} rolou os dados...`
                : `${espelho.nome} está montando a jogada`}
            </p>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={limpar}
            disabled={bloqueado || !dadosLocais.length}
            className="text-sm text-game-muted hover:text-game-primary disabled:opacity-40 min-h-11 px-1"
          >
            limpar
          </button>

          {jogada ? (
            souMestre ? (
              // A jogada do proprio mestre e auto-validada: ele nao espera
              // ninguem, e o botao dele continua sendo o de sempre.
              <button
                type="button"
                onClick={enviar}
                disabled={enviando}
                data-testid="bandeja-enviar"
                className={cn(
                  'min-h-11 px-6 rounded-xl font-semibold',
                  'bg-emerald-600 text-white active:translate-y-px transition-transform',
                  'disabled:opacity-40 disabled:cursor-not-allowed'
                )}
              >
                {enviando ? 'Enviando...' : 'Enviar ao chat'}
              </button>
            ) : (
              // O jogador NAO envia a propria jogada (decisao 3): a mesa ja viu
              // o numero, e quem fecha o momento e o mestre. Se ele nao agir, o
              // prazo do servidor publica sozinho — sem cronometro daqui.
              <p
                className="text-xs text-game-muted"
                role="status"
                data-testid="bandeja-aguardando-mestre"
              >
                Aguardando o mestre validar...
              </p>
            )
          ) : (
            <button
              type="button"
              onClick={rolar}
              disabled={bloqueado || !dadosLocais.length || !podeRolarSemMestre}
              data-testid="bandeja-rolar"
              className={cn(
                'min-h-11 px-6 rounded-xl font-semibold',
                'bg-violet-600 text-white active:translate-y-px transition-transform',
                'disabled:opacity-40 disabled:cursor-not-allowed'
              )}
            >
              {!podeRolarSemMestre
                ? 'O mestre precisa estar na mesa'
                : rolando ? 'Rolando...' : 'Rolar'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default DiceTray
